import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Crown, Sparkles, Tag } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import PaymentMethodDialog from '@/components/PaymentMethodDialog';
import DiscordUsernameField from '@/components/DiscordUsernameField';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const PREMIUM_META = {
  label: 'Premium VIP',
  subtitle: 'All access · Every script · Private leaks & VIP tools · Lifetime.',
  image: '/vip-hero.png',
  accent: 'rgb(var(--brand-rgb))',
  accentSoft: 'rgb(var(--brand-rgb)/0.35)',
};

export default function VipCheckout() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, loading } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [sale, setSale] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validating, setValidating] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [dcName, setDcName] = useState('');
  useEffect(() => { if (user?.discord_username) setDcName((n) => n || user.discord_username); }, [user]);
  const dcClean = dcName.trim().replace(/^@/, '').toLowerCase();
  const dcValid = /^[a-z0-9_.]{2,32}$/.test(dcClean) && !dcClean.includes('..');

  // Only Premium VIP is available — any ?tier=matrix|zen is coerced to premium.
  const tierId = 'premium';
  const meta = PREMIUM_META;

  useEffect(() => {
    if (loading) return;
    if (!user) nav(`/login?next=/checkout/vip`);
  }, [user, loading, nav, loc.search]);
  useEffect(() => {
    api.get('/vip/tiers').then((r) => setTiers(r.data)).catch(() => setTiers([]));
    api.get('/sale/current').then((r) => setSale(r.data)).catch(() => setSale({ active: false }));
  }, []);

  const tier = useMemo(
    () => (tiers || []).find((t) => t.id === tierId) || null,
    [tiers]
  );
  const basePrice = tier?.price_aud || 60;
  const salePrice = useMemo(() => {
    if (!sale?.active) return null;
    const applies = sale.applies_to || 'all';
    if (applies !== 'all' && applies !== 'vip') return null;
    if (sale.type === 'fixed') return Math.max(1, basePrice - Number(sale.value || 0));
    if (sale.type === 'percent') return Math.max(1, Math.round(basePrice * (1 - Number(sale.value || 0) / 100)));
    return null;
  }, [sale, basePrice]);
  const payingPrice = salePrice ?? basePrice;

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post('/payments/validate-discount', { code: discountCode.trim(), kind: 'vip' });
      setDiscountApplied(data);
      toast.success(`Code applied — ${data.type === 'percent' ? data.value + '% off' : '$' + data.value + ' off'}`);
    } catch (e) {
      setDiscountApplied(null);
      toast.error(formatApiError(e.response?.data?.detail) || 'Invalid code');
    } finally { setValidating(false); }
  };

  if (!user) return null;

  return (
    <div className="bg-grain relative min-h-screen text-white">
      <Navbar />
      <div className="orb" style={{ top: '10%', left: '50%', transform: 'translateX(-50%)', width: 520, height: 520, background: `radial-gradient(circle, ${meta.accentSoft}, transparent 70%)` }} />
      <div className="max-w-2xl mx-auto pt-28 pb-20 px-6 relative">
        <div className="glass-strong rounded-3xl p-6 md:p-9 text-center" data-testid={`vip-checkout-${tierId}`}>
          <img
            src={meta.image}
            alt={meta.label}
            className="mx-auto w-56 md:w-64 h-auto select-none drop-shadow-[0_25px_45px_rgba(0,0,0,0.55)]"
            draggable={false}
            style={{ filter: `drop-shadow(0 0 40px ${meta.accentSoft})` }}
          />
          <Crown className="h-8 w-8 mx-auto mt-3" style={{ color: meta.accent, filter: `drop-shadow(0 0 14px ${meta.accentSoft})` }} />
          <p className="mt-4 text-xs uppercase tracking-[0.35em] font-mono" style={{ color: meta.accent }}>{meta.label} · Lifetime</p>
          <h1 className="font-display font-black text-3xl md:text-4xl mt-3 tracking-tighter">Confirm your purchase</h1>
          <p className="mt-2 text-sm text-white/60">{meta.subtitle}</p>
          <div className="mt-6 flex items-baseline justify-center gap-2 flex-wrap">
            {salePrice ? (
              <>
                <span className="font-display font-black text-white text-5xl md:text-6xl tracking-tighter">${salePrice}</span>
                <span className="text-white/50">USD · one-time</span>
                <span className="w-full flex items-center justify-center gap-2 mt-1">
                  <span className="text-white/40 text-xl line-through tracking-tighter">${basePrice}</span>
                  <span className="inline-flex items-center rounded-full bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 text-[10px] font-mono font-semibold uppercase tracking-widest px-2 py-0.5">
                    Save ${basePrice - salePrice} · sale auto-applied
                  </span>
                </span>
              </>
            ) : (
              <>
                <span className="font-display font-black text-white text-5xl md:text-6xl tracking-tighter">${basePrice}</span>
                <span className="text-white/50">USD · one-time</span>
              </>
            )}
          </div>
          {discountApplied && (
            <div className="mt-3 text-sm text-emerald-400" data-testid="vip-discount-applied">
              ✓ <span className="font-mono">{discountApplied.code}</span> applied — {discountApplied.type === 'percent' ? `${discountApplied.value}% off` : `$${discountApplied.value} USD off`}
            </div>
          )}

          <div className="mt-6 flex items-stretch gap-2">
            <div className="relative flex-1">
              <Tag className="h-4 w-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="vip-discount-input"
                type="text" value={discountCode}
                onChange={(e) => { setDiscountCode(e.target.value); setDiscountApplied(null); }}
                placeholder="Discount code (optional)"
                className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white placeholder-white/30 font-mono uppercase text-sm"
              />
            </div>
            <button
              data-testid="vip-apply-discount-btn"
              onClick={applyDiscount} disabled={validating || !discountCode.trim()}
              className="px-5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm text-white disabled:opacity-50 transition-colors"
            >{validating ? '…' : 'Apply'}</button>
          </div>

          <DiscordUsernameField value={dcName} onChange={setDcName} valid={dcValid} />

          <button
            data-testid="vip-proceed-btn"
            onClick={() => (dcValid ? setPayOpen(true) : toast.error('Enter your Discord username first'))}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-lg text-[#0a0a0a] transition-shadow"
            style={{ background: meta.accent, boxShadow: `0 0 0 rgba(0,0,0,0)` }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 36px ${meta.accentSoft}`; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 0 rgba(0,0,0,0)'; }}
          >
            <Sparkles className="h-4 w-4" /> Continue to secure checkout
          </button>
          <p className="mt-4 text-xs text-white/40">Pick card or PayPal on the next step. Payment is processed in USD.</p>

          <PaymentMethodDialog
            open={payOpen}
            onOpenChange={setPayOpen}
            amount={payingPrice}
            kind="vip"
            vipTier={tierId}
            discountCode={discountApplied ? discountApplied.code : null}
            discordUsername={dcClean}
          />
        </div>
      </div>
    </div>
  );
}
